import { z } from 'zod';
export const analyticsPlatformSchema = z.enum(['all', 'ios', 'android', 'web']);
export const clientAnalyticsPlatformSchema = analyticsPlatformSchema.exclude(['all']);
export const analyticsMetricStateSchema = z.enum([
    'ready',
    'awaiting_data',
    'insufficient_maturity',
    'not_applicable',
]);
export const analyticsSectionSchema = z.enum([
    'overview',
    'global-usage',
    'average-per-user',
    'preference-profiles',
    'listing-inventory',
    'alerts-engagement',
    'scraper-health',
]);
export const analyticsRouteNameSchema = z.enum([
    'auth-sign-in',
    'auth-verify',
    'onboarding',
    'matches',
    'saved',
    'preferences',
    'preference-new',
    'preference-edit',
    'settings',
    'listings',
    'unknown',
]);
const eventBaseSchema = z.object({
    eventId: z.uuid(),
    sessionId: z.uuid(),
    occurredAt: z.iso.datetime({ offset: true }),
    platform: clientAnalyticsPlatformSchema,
    appVersion: z.string().trim().min(1).max(40),
});
export const clientAnalyticsEventSchema = z.discriminatedUnion('name', [
    eventBaseSchema.extend({ name: z.literal('session_started') }),
    eventBaseSchema.extend({ name: z.literal('session_heartbeat') }),
    eventBaseSchema.extend({ name: z.literal('session_ended') }),
    eventBaseSchema.extend({
        name: z.literal('screen_viewed'),
        routeName: analyticsRouteNameSchema,
    }),
    eventBaseSchema.extend({
        name: z.literal('listing_opened'),
        listingId: z.number().int().positive(),
        source: z.enum(['yad2', 'facebook']),
        profileId: z.uuid().nullable().optional(),
    }),
    eventBaseSchema.extend({
        name: z.literal('listing_saved'),
        listingId: z.number().int().positive(),
    }),
    eventBaseSchema.extend({
        name: z.literal('listing_unsaved'),
        listingId: z.number().int().positive(),
    }),
    eventBaseSchema.extend({
        name: z.literal('push_alert_opened'),
        alertDeliveryId: z.uuid(),
        profileId: z.uuid().nullable().optional(),
    }),
]);
export const analyticsEventBatchSchema = z.object({
    events: z.array(clientAnalyticsEventSchema).min(1).max(100),
});
export const analyticsRangeSchema = z.object({
    from: z.iso.date(),
    toExclusive: z.iso.date(),
});
export const analyticsFilterSchema = analyticsRangeSchema.extend({
    platform: analyticsPlatformSchema.default('all'),
    groupId: z.coerce.number().int().positive().nullable().default(null),
    bypassCache: z.boolean().default(false),
});
const analyticsMetricUnitSchema = z.enum(['count', 'percent', 'minutes', 'days', 'currency', 'ratio']);
const analyticsMetricBaseSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    unit: analyticsMetricUnitSchema,
    previousValue: z.number().nullable().optional(),
    deltaPercent: z.number().nullable().optional(),
    note: z.string().nullable().optional(),
});
export const analyticsMetricSchema = z.discriminatedUnion('state', [
    analyticsMetricBaseSchema.extend({
        state: z.literal('ready'),
        value: z.number(),
        formattedValue: z.string().min(1),
    }),
    analyticsMetricBaseSchema.extend({
        state: z.literal('awaiting_data'), value: z.null(), formattedValue: z.null(),
    }),
    analyticsMetricBaseSchema.extend({
        state: z.literal('insufficient_maturity'), value: z.null(), formattedValue: z.null(),
    }),
    analyticsMetricBaseSchema.extend({
        state: z.literal('not_applicable'), value: z.null(), formattedValue: z.null(),
    }),
]);
export const analyticsFailureDetailSchema = z.object({
    source: z.string().min(1),
    reason: z.string().min(1),
    action: z.string().min(1).nullable().optional(),
});
export const analyticsSeriesPointSchema = z.object({
    date: z.iso.date(),
    value: z.number(),
    secondaryValue: z.number().nullable().optional(),
    failureDetails: z.array(analyticsFailureDetailSchema).optional(),
});
export const analyticsBreakdownItemSchema = z.object({
    key: z.string(),
    label: z.string(),
    value: z.number(),
    count: z.number().nullable().optional(),
    secondaryLabel: z.string().nullable().optional(),
    secondaryValue: z.number().nullable().optional(),
    secondaryCount: z.number().nullable().optional(),
});
export const analyticsReportDataSchema = z.object({
    metrics: z.array(analyticsMetricSchema),
    series: z.record(z.string(), z.array(analyticsSeriesPointSchema)).default({}),
    breakdowns: z.record(z.string(), z.array(analyticsBreakdownItemSchema)).default({}),
});
export const analyticsReportEnvelopeSchema = z.object({
    section: analyticsSectionSchema,
    range: analyticsRangeSchema,
    platform: analyticsPlatformSchema,
    timeZone: z.literal('Asia/Jerusalem'),
    generatedAt: z.iso.datetime({ offset: true }),
    cacheExpiresAt: z.iso.datetime({ offset: true }),
    data: analyticsReportDataSchema,
});
export const analyticsScraperSourceSchema = z.enum(['yad2', 'facebook']);
const analyticsScraperConfigFieldsSchema = z.object({
    enabled: z.boolean(),
    windowStart: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    windowEnd: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    runOnMonday: z.boolean(),
    runOnTuesday: z.boolean(),
    runOnWednesday: z.boolean(),
    runOnThursday: z.boolean(),
    runOnFriday: z.boolean(),
    runOnSaturday: z.boolean(),
    runOnSunday: z.boolean(),
    maxListingAgeDays: z.number().int().min(1).max(365),
    hourlyFrequency: z.number().int().min(1).max(24),
});
export const analyticsScraperConfigSchema = analyticsScraperConfigFieldsSchema.extend({
    source: analyticsScraperSourceSchema,
});
export const analyticsScraperConfigUpdateSchema = analyticsScraperConfigFieldsSchema;
export const analyticsScraperConfigListSchema = z.array(analyticsScraperConfigSchema);
export const analyticsSessionSchema = z.discriminatedUnion('authenticated', [
    z.object({ authenticated: z.literal(false) }),
    z.object({
        authenticated: z.literal(true),
        user: z.object({ id: z.uuid(), email: z.email() }),
    }),
]);
